// Leads by webhook (D-158, INT-12), JustDial first, on the integration's own page: the company's address to give the
// sender, waiting until its first lead arrives; who gets new customers; which fields hold the number, name, email and
// city when Voxowa cannot tell; and the latest leads, each with what arrived and what became of it. IndiaMART (D-176)
// is set up by the seller in IndiaMART's Lead Manager, its fields are always IndiaMART's own, and it has a daily read
// with the Pull API key.
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useMember } from "../app/session";
import { Card, ConfirmButton, ErrorNote, Loading, useAction, useLoad, useToast } from "../app/ui";
import { fmtDateTime } from "../lib/format";
import { type Integration, WEBHOOK_FIELD_LABELS } from "../lib/integrations";
import type {
  IndiaMartPull as IndiaMartPullState,
  IndiaMartRead,
  WebhookFields,
  WebhookLeadRow,
  WebhookRetried,
  WebhookState,
} from "../lib/types";
import { WhoGets } from "./FacebookLeadAds";

export function LeadWebhook({ integration, onChanged }: { integration: Integration; onChanged: () => void }) {
  const { api, tz } = useMember();
  const state = useLoad(() => api.webhookState(integration.key), [api, integration.key]);
  const { busy, run } = useAction();
  const changed = () => {
    onChanged();
    void state.reload();
  };
  const waiting = state.data?.status === "waiting";
  // Waiting for the first lead: looked at again every 15 seconds, so the page turns Connected by itself.
  useEffect(() => {
    if (!waiting) return;
    const timer = setInterval(() => {
      void state.reload().then(() => onChanged());
    }, 15_000);
    return () => clearInterval(timer);
  }, [waiting]);

  if (state.loading && !state.data) return <Loading />;
  const s = state.data;
  if (!s) return <ErrorNote error={state.error} />;
  const name = integration.name;

  if (s.status === "disconnected") {
    return (
      <Card>
        <div className="stack">
          <ErrorNote error={state.error} />
          <p>
            Voxowa gives your company its own address for {name}. {name}{" "}
            sends each new enquiry to it, and the lead reaches a telecaller within a minute.
          </p>
          <div className="actions">
            <button
              type="button"
              className="primary"
              disabled={busy}
              onClick={() =>
                void run(() => api.webhookConnect(integration.key), "Your address is ready.").then((ok) =>
                  ok && changed()
                )}
            >
              Get the address
            </button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="stack webhook">
      <ErrorNote error={state.error} />
      <Card title={`Your ${name} address`}>
        <Address state={s} integration={integration} onChanged={changed} />
      </Card>
      {s.pull && (
        <Card title="Daily check with the Pull API">
          <IndiaMartPull pull={s.pull} onChanged={changed} />
        </Card>
      )}
      <Card>
        <WhoGets
          assign={s.assign}
          onChanged={changed}
          save={(mode, members) => api.webhookSetAssignment(integration.key, mode, members)}
          hint={`Each ${name} enquiry from a number Voxowa does not know yet. A customer Voxowa knows keeps their owner.`}
        />
      </Card>
      {integration.key !== "indiamart" && (
        <Card title="Fields">
          <FieldsChooser key={JSON.stringify(s.fields)} integration={integration} state={s} onChanged={changed} />
        </Card>
      )}
      <Card title="Latest leads">
        <WebhookLeads leads={s.leads} tz={tz} name={name} />
      </Card>
    </div>
  );
}

/** The address with Copy, what to do with it, how it is going, and a new address or turning it off. */
function Address(
  { state, integration, onChanged }: { state: WebhookState; integration: Integration; onChanged: () => void },
) {
  const { api, tz } = useMember();
  const { busy, run } = useAction();
  const toast = useToast();
  const name = integration.name;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(state.address ?? "");
      toast("Copied.");
    } catch {
      toast("Select the address and copy it.", true);
    }
  };
  return (
    <div className="stack">
      <div className="webhook-address">
        <input
          readOnly
          value={state.address ?? ""}
          aria-label={`Your ${name} address`}
          onFocus={(e) => e.target.select()}
        />
        <button type="button" className="primary small" onClick={() => void copy()}>Copy</button>
      </div>
      <ol className="webhook-steps">
        <li>Copy the address.</li>
        {integration.key === "indiamart"
          ? (
            <>
              <li>
                In your IndiaMART seller account, open Lead Manager, then the ⋮ menu, Import/Export Leads and Push API.
              </li>
              <li>
                Choose Other as your CRM, name it Voxowa, paste the address and save. IndiaMART sends an OTP to your
                primary mobile to confirm it.
              </li>
            </>
          )
          : (
            <li>
              Send it to your {name} account manager, or to {name} support from your {name}{" "}
              account, and ask them to send your leads to it.
            </li>
          )}
        <li>
          Until the first lead arrives, this page says Waiting for the first lead. Then it says Connected, and each new
          enquiry reaches a telecaller within a minute.
        </li>
      </ol>
      <p className="faint">
        Keep the address to yourselves: anyone who has it can send you leads. If it gets out, make a new one; the old
        one stops working at once.
      </p>
      <dl className="integration-facts">
        <dt>Turned on</dt>
        <dd>{state.connected_at ? fmtDateTime(state.connected_at, tz) : "–"}</dd>
        <dt>Latest lead</dt>
        <dd>{state.last_lead_at ? fmtDateTime(state.last_lead_at, tz) : "None yet"}</dd>
        <dt>New enquiries</dt>
        <dd>
          {state.counts.today} today, {state.counts.week} this week
          {state.counts.skipped_week
            ? <span className="faint">{` · ${state.counts.skipped_week} skipped`}</span>
            : null}
        </dd>
      </dl>
      <div className="actions">
        <ConfirmButton
          label="Turn off"
          confirm={`Turn ${name} off? Its address stops working at once; turning it on again gives a new one.`}
          disabled={busy}
          onConfirm={() =>
            void run(() => api.disconnectIntegration(integration.key), `${name} is off.`).then((ok) =>
              ok && onChanged()
            )}
        />
        <ConfirmButton
          label="New address"
          confirm={`Make a new address? The old one stops working at once, and ${name} needs the new one.`}
          disabled={busy}
          onConfirm={() =>
            void run(() => api.webhookNewAddress(integration.key), "A new address is ready.").then((ok) =>
              ok && onChanged()
            )}
        />
      </div>
    </div>
  );
}

/** IndiaMART's Pull API (D-176): the key, kept only once IndiaMART takes it, and what each morning's read found. */
function IndiaMartPull({ pull, onChanged }: { pull: IndiaMartPullState; onChanged: () => void }) {
  const { api, tz } = useMember();
  const { busy, run } = useAction();
  const toast = useToast();
  const [key, setKey] = useState("");
  const read = (r: IndiaMartRead) => {
    if (r.outcome === "leads") {
      toast(r.new ? `${r.new} new ${r.new === 1 ? "lead" : "leads"} came in.` : "No new leads: all are in Voxowa.");
    } else if (r.outcome === "none") toast("No new leads.");
    else if (r.message) toast(r.message, true);
  };

  if (!pull.key) {
    return (
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void run(() => api.indiamartSaveKey(key).then(read)).then((ok) => {
            if (!ok) return;
            setKey("");
            onChanged();
          });
        }}
      >
        <p>
          Each morning Voxowa also reads the last day's IndiaMART leads with your Pull API key, so a lead the push
          missed still comes in. A lead already in Voxowa never comes in twice.
        </p>
        <ol className="webhook-steps">
          <li>In IndiaMART's Lead Manager, open the ⋮ menu, Import/Export Leads and Pull API, and generate the key.</li>
          <li>Paste it here. Voxowa checks it with IndiaMART and brings in the last 7 days' leads.</li>
        </ol>
        <label className="field">
          <span>Pull API key</span>
          <input
            type="password"
            aria-label="Pull API key"
            autoComplete="off"
            spellCheck={false}
            value={key}
            onChange={(e) => setKey(e.target.value)}
          />
        </label>
        <div className="actions">
          <button type="submit" className="primary" disabled={busy || !key.trim()}>Save the key</button>
        </div>
      </form>
    );
  }

  const problem = pull.outcome === "refused" || pull.outcome === "error" || pull.outcome === "busy";
  return (
    <div className="stack">
      {problem && pull.message && <p className="danger-text">{pull.message}</p>}
      <dl className="integration-facts">
        <dt>Key</dt>
        <dd>Saved{pull.key_set_at ? ` ${fmtDateTime(pull.key_set_at, tz)}` : ""}</dd>
        <dt>Last check</dt>
        <dd>
          {pull.checked_at ? fmtDateTime(pull.checked_at, tz) : "Not yet"}
          {pull.outcome === "leads" || pull.outcome === "none"
            ? (
              <span className="faint">
                {` · ${pull.found ? `${pull.found} new` : "nothing new"}`}
                {pull.known ? `, ${pull.known} already in Voxowa` : ""}
              </span>
            )
            : null}
        </dd>
        <dt>Next</dt>
        <dd>Every morning at 6:15 am</dd>
      </dl>
      <p className="faint">
        IndiaMART stops a key that is not used for 7 days; the daily check keeps it in use. If IndiaMART says the key is
        not accepted, generate a new one there and add it here.
      </p>
      <div className="actions">
        <button
          type="button"
          className="small"
          disabled={busy}
          onClick={() => void run(() => api.indiamartCheckNow().then(read)).then((ok) => ok && onChanged())}
        >
          Check now
        </button>
        <ConfirmButton
          label={problem ? "Add a new key" : "Remove the key"}
          confirm="Remove the Pull API key? The daily check stops; leads IndiaMART pushes still come in."
          disabled={busy}
          onConfirm={() =>
            void run(() => api.indiamartRemoveKey(), "The key is removed.").then((ok) => ok && onChanged())}
        />
      </div>
    </div>
  );
}

/** "3 came in; 1 still can't be read", after the leads that waited were tried again. */
function retriedText(r: WebhookRetried): string {
  if (r.tried === 0) return "Saved.";
  const left = r.unreadable ? `; ${r.unreadable} still can't be read` : "";
  return `${r.brought_in} of ${r.tried} came in${left}.`;
}

/** Which field holds the number, name, email and city, when Voxowa cannot tell by their names. */
function FieldsChooser(
  { integration, state, onChanged }: { integration: Integration; state: WebhookState; onChanged: () => void },
) {
  const { api } = useMember();
  const { busy, run } = useAction();
  const toast = useToast();
  const [fields, setFields] = useState<WebhookFields>(state.fields);
  const keys = Object.keys(WEBHOOK_FIELD_LABELS) as (keyof WebhookFields)[];
  const changed = keys.some((k) => (fields[k] ?? "") !== (state.fields[k] ?? ""));
  const stuck = state.counts.unreadable + state.counts.waiting;
  const options = (
    k: keyof WebhookFields,
  ) => [...new Set([...state.seen_fields, ...(state.fields[k] ? [state.fields[k]!] : [])])];
  const done = (r: WebhookRetried) => {
    toast(retriedText(r));
    onChanged();
  };
  return (
    <div className="stack">
      {state.counts.unreadable > 0
        ? (
          <p className="danger-text">
            {state.counts.unreadable} {state.counts.unreadable === 1 ? "lead" : "leads"}{" "}
            could not be read: Voxowa found no phone number in{" "}
            {state.counts.unreadable === 1 ? "it" : "them"}. Open one under Latest leads to see what arrived, then
            choose the field that holds the number.
          </p>
        )
        : (
          <p className="muted">
            Voxowa finds the phone number, name, email and city by their usual names. If {integration.name}{" "}
            names one differently, choose its field here.
          </p>
        )}
      <div className="webhook-fields-chooser">
        {keys.map((k) => (
          <label className="field" key={k}>
            <span>{WEBHOOK_FIELD_LABELS[k]}</span>
            <select
              value={fields[k] ?? ""}
              onChange={(e) => setFields({ ...fields, [k]: e.target.value || undefined })}
            >
              <option value="">Found by its name</option>
              {options(k).map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </label>
        ))}
      </div>
      <div className="actions">
        {stuck > 0 && !changed && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void run(async () => done(await api.webhookRetry(integration.key)))}
          >
            Try again
          </button>
        )}
        {changed && (
          <button
            type="button"
            className="primary"
            disabled={busy}
            onClick={() => void run(async () => done(await api.webhookSetFields(integration.key, fields)))}
          >
            {stuck > 0 ? "Save and bring them in" : "Save"}
          </button>
        )}
      </div>
    </div>
  );
}

const STATUS: Record<WebhookLeadRow["status"], string> = {
  done: "In",
  pending: "Waiting",
  skipped: "Skipped",
  unreadable: "Not read",
};

/** A lead that did not come in, by whatever looks like its name. */
function guessName(row: WebhookLeadRow): string {
  return row.fields.find(([k]) => /name|customer/i.test(k))?.[1] ?? "Lead";
}

function WebhookLeads({ leads, tz, name }: { leads: WebhookLeadRow[]; tz: string; name: string }) {
  if (leads.length === 0) return <p className="muted">No lead from {name} yet.</p>;
  return (
    <div className="table-wrap">
      <table>
        <tbody>
          {leads.map((l) => (
            <tr key={l.id}>
              <td className="faint nowrap">{fmtDateTime(l.received_at, tz)}</td>
              <td>
                {l.lead_id ? <Link to={`/leads/${l.lead_id}`}>{l.name ?? l.phone ?? "Customer"}</Link> : guessName(l)}
                {l.fields.length > 0 && (
                  <details className="webhook-arrived">
                    <summary>What arrived</summary>
                    <dl>
                      {l.fields.map(([k, v], i) => (
                        <div key={`${k}-${i}`}>
                          <dt>{k}</dt>
                          <dd>{v}</dd>
                        </div>
                      ))}
                    </dl>
                  </details>
                )}
              </td>
              <td>
                <span className={l.status === "done" ? "" : l.status === "pending" ? "faint" : "danger-text"}>
                  {STATUS[l.status]}
                </span>
                <div className="faint">
                  {l.status === "done"
                    ? l.enquiry ? (l.owner ? `To ${l.owner}` : "Nobody yet") : "Already in Voxowa"
                    : l.reason ?? ""}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
